import { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';

/**
 * Custom hook that debounces an event handler callback.
 *
 * This implementation uses a ref to store the latest callback to avoid stale closures,
 * ensuring the debounced function always calls the most recent version of the callback.
 *
 * Since this hook is only used for DOM event handlers in our codebase (specifically
 * for the search input in PhrasebookPageContent), we use a specific Event type rather
 * than generics to avoid TypeScript variance issues.
 *
 * @param callback - The event handler function to debounce
 * @param duration - The debounce delay in milliseconds (default: 1000)
 * @returns A debounced version of the callback with cancel and flush methods
 */
export const useDebounce = (callback: (event: Event) => void, duration = 1000) => {
  const ref = useRef<((event: Event) => void) | null>(null);

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  return useMemo(() => {
    /* istanbul ignore next: tested manually */
    const func = (event: Event) => {
      ref.current?.(event);
    };

    return debounce(func, duration);
  }, [duration]);
};
